import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityId,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit';
import { RecipeDetailHeader } from '../../api/models/recipe-detail.models';
import { RecipeIngredientCategory } from '../../api/models/recipe-ingredient-category.models';
import { RecipeCustomerIngredientDiary, RecipeIngredientDiary } from '../../api/models/recipe-ingredient-diary.models';
import { RecipeIngredientHeader } from '../../api/models/recipe-ingredient-header.models';
import { RecipeIngredientProduct } from '../../api/models/recipe-ingredient-product.models';
import { filterObjects, getSortComparer, normalizeProductKey } from '../../helpers';
import { RootState } from '../store';

const ingredientsAdapter = createEntityAdapter<RecipeIngredientHeader>({
  selectId: (data: RecipeIngredientHeader) => data.RecipeIngredientHeaderId,
  sortComparer: (a: RecipeIngredientHeader, b: RecipeIngredientHeader) => {
    const compareCategorized = getSortComparer<RecipeIngredientHeader, boolean>(
      (r) => r.CategoryName === 'Uncategorized'
    );
    const compareCategoryName = getSortComparer<RecipeIngredientHeader, string>((r) => r.CategoryName ?? '');
    const compareName = getSortComparer<RecipeIngredientHeader, string>((r) => r.IngredientName);
    return compareCategorized(a, b) || compareCategoryName(a, b) || compareName(a, b);
  },
});

const ingredientProductsAdapter = createEntityAdapter<RecipeIngredientProduct>({
  selectId: (data: RecipeIngredientProduct) => data.RecipeIngredientProductId,
});

const ingredientCategoriesAdapter = createEntityAdapter<RecipeIngredientCategory>({
  selectId: (data: RecipeIngredientCategory) => data.RecipeIngredientCategoryId,
  sortComparer: getSortComparer((d) => d.CategoryName),
});

const ingredientDiariesAdapter = createEntityAdapter<RecipeIngredientDiary>({
  selectId: (data: RecipeIngredientDiary) => data.RecipeIngredientDiaryId,
  sortComparer: (a, b) => new Date(b.ChangeDate).getTime() - new Date(a.ChangeDate).getTime(),
});

const ingredientCustomerDiariesAdapter = createEntityAdapter<RecipeCustomerIngredientDiary>({
  selectId: (data: RecipeCustomerIngredientDiary) => data.RecipeCustomerIngredientDiaryId,
  sortComparer: (a, b) => new Date(b.ChangeDate).getTime() - new Date(a.ChangeDate).getTime(),
});

const ingredientRecipesAdapter = createEntityAdapter<RecipeDetailHeader>({
  selectId: (data) => data.RecipeDetail.RecipeDetailId,
});

type IngredientsState = {
  gridParameters: { customerId: string } | undefined;

  isIngredientsLoading: boolean;
  ingredients: EntityState<RecipeIngredientHeader>;
  isIngredientProductsLoading: boolean;
  ingredientProducts: EntityState<RecipeIngredientProduct>;
  isIngredientCategoriesLoading: boolean;
  ingredientCategories: EntityState<RecipeIngredientCategory>;
  isIngredientDiariesLoading: boolean;
  ingredientDiaries: EntityState<RecipeIngredientDiary>;
  isIngredientCustomerDiariesLoading: boolean;
  ingredientCustomerDiaries: EntityState<RecipeCustomerIngredientDiary>;
  isIngredientRecipesLoading: boolean;
  ingredientRecipes: EntityState<RecipeDetailHeader>;
};

const initialState: IngredientsState = {
  gridParameters: undefined,
  isIngredientsLoading: true,
  ingredients: ingredientsAdapter.getInitialState(),
  isIngredientProductsLoading: true,
  ingredientProducts: ingredientProductsAdapter.getInitialState(),
  isIngredientCategoriesLoading: true,
  ingredientCategories: ingredientCategoriesAdapter.getInitialState(),
  isIngredientDiariesLoading: true,
  ingredientDiaries: ingredientDiariesAdapter.getInitialState(),
  isIngredientCustomerDiariesLoading: true,
  ingredientCustomerDiaries: ingredientCustomerDiariesAdapter.getInitialState(),
  isIngredientRecipesLoading: false,
  ingredientRecipes: ingredientRecipesAdapter.getInitialState(),
};

export const ingredientsSlice = createSlice({
  initialState,
  name: 'ingredients',
  reducers: {
    resetIngredients: (state: IngredientsState) => {
      state.ingredients = initialState.ingredients;
      state.gridParameters = initialState.gridParameters;
    },
    resetIngredientDetails: (state: IngredientsState) => {
      state.ingredientProducts = initialState.ingredientProducts;
      state.ingredientRecipes = initialState.ingredientRecipes;
    },
    setGridParameters: (state: IngredientsState, action: PayloadAction<{ customerId: string } | undefined>) => {
      state.gridParameters = action.payload;
    },
    resetGridParameters: (state: IngredientsState) => {
      state.gridParameters = initialState.gridParameters;
    },
    setIsIngredientsLoading: (state: IngredientsState, action: PayloadAction<boolean>) => {
      state.isIngredientsLoading = action.payload;
    },
    setIngredient: (state: IngredientsState, action: PayloadAction<RecipeIngredientHeader>) => {
      ingredientsAdapter.setOne(state.ingredients, action.payload);
    },
    setIngredients: (state: IngredientsState, action: PayloadAction<RecipeIngredientHeader[]>) => {
      ingredientsAdapter.setAll(state.ingredients, action.payload);
    },
    removeIngredient: (state: IngredientsState, action: PayloadAction<string>) => {
      ingredientsAdapter.removeOne(state.ingredients, action.payload);
    },
    setIsIngredientProductsLoading: (state: IngredientsState, action: PayloadAction<boolean>) => {
      state.isIngredientProductsLoading = action.payload;
    },
    setIngredientProduct: (
      state: IngredientsState,
      action: PayloadAction<{ product: RecipeIngredientProduct; isCustomerPrimary: boolean }>
    ) => {
      ingredientProductsAdapter.setOne(state.ingredientProducts, action.payload.product);
      if (action.payload.isCustomerPrimary) {
        const workingIds = [...state.ingredientProducts.ids];
        const idx = workingIds.indexOf(action.payload.product.RecipeIngredientProductId);
        const ids = workingIds.splice(idx, 1);
        state.ingredientProducts.ids = [...ids, ...workingIds];
      }
    },
    setManyIngredientProducts: (state: IngredientsState, action: PayloadAction<RecipeIngredientProduct[]>) => {
      ingredientProductsAdapter.setMany(state.ingredientProducts, action.payload);
    },
    setIngredientProducts: (state: IngredientsState, action: PayloadAction<RecipeIngredientProduct[]>) => {
      ingredientProductsAdapter.setAll(state.ingredientProducts, action.payload);
    },
    removeIngredientProduct: (state: IngredientsState, action: PayloadAction<string>) => {
      ingredientProductsAdapter.removeOne(state.ingredientProducts, action.payload);
    },
    setIsIngredientCategoriesLoading: (state: IngredientsState, action: PayloadAction<boolean>) => {
      state.isIngredientCategoriesLoading = action.payload;
    },
    setIngredientCategory: (state: IngredientsState, action: PayloadAction<RecipeIngredientCategory>) => {
      ingredientCategoriesAdapter.setOne(state.ingredientCategories, action.payload);
    },
    setIngredientCategories: (state: IngredientsState, action: PayloadAction<RecipeIngredientCategory[]>) => {
      ingredientCategoriesAdapter.setAll(state.ingredientCategories, action.payload);
    },
    removeIngredientCategory: (state: IngredientsState, action: PayloadAction<string>) => {
      ingredientCategoriesAdapter.removeOne(state.ingredientCategories, action.payload);
    },
    setIsIngredientDiariesLoading: (state: IngredientsState, action: PayloadAction<boolean>) => {
      state.isIngredientDiariesLoading = action.payload;
    },
    setIngredientDiaries: (state: IngredientsState, action: PayloadAction<RecipeIngredientDiary[]>) => {
      ingredientDiariesAdapter.setAll(state.ingredientDiaries, action.payload);
    },
    setIsIngredientCustomerDiariesLoading: (state: IngredientsState, action: PayloadAction<boolean>) => {
      state.isIngredientCustomerDiariesLoading = action.payload;
    },
    setIngredientCustomerDiaries: (state: IngredientsState, action: PayloadAction<RecipeCustomerIngredientDiary[]>) => {
      ingredientCustomerDiariesAdapter.setAll(state.ingredientCustomerDiaries, action.payload);
    },
    removeRecipeIngredientCustomerDiary: (state: IngredientsState, action: PayloadAction<string>) => {
      ingredientCustomerDiariesAdapter.removeOne(state.ingredientCustomerDiaries, action.payload);
    },
    updateCategoryIsCollapsed: (
      state: IngredientsState,
      action: PayloadAction<{ categoryId: string; isCollapsed: boolean }>
    ) => {
      const ingredients = Object.values(state.ingredients.entities);
      ingredients.forEach((c) => {
        if (c && c.RecipeIngredientCategoryId === action.payload.categoryId) {
          c.IsCollapsed = action.payload.isCollapsed;
        }
      });
    },
    updateAllCategoryIsCollapsed: (state: IngredientsState, action: PayloadAction<boolean>) => {
      const recipes = Object.values(state.ingredients.entities);
      recipes.forEach((c) => {
        if (c) {
          c.IsCollapsed = action.payload;
        }
      });
    },
    setIngredientRecipes: (state: IngredientsState, action: PayloadAction<RecipeDetailHeader[]>) => {
      ingredientRecipesAdapter.setAll(state.ingredientRecipes, action.payload);
    },
    setIsIngredientRecipesLoading: (state: IngredientsState, action: PayloadAction<boolean>) => {
      state.isIngredientRecipesLoading = action.payload;
    },
    setUsdaProductStatusType: (state: IngredientsState, action: PayloadAction<Record<string, number>>) => {
      const statusMap = action.payload;

      Object.values(state.ingredientProducts.entities).forEach((ingredient) => {
        if (ingredient?.Product?.ProductKey) {
          const statusType = statusMap[normalizeProductKey(ingredient.Product.ProductKey)];
          if (statusType !== undefined) {
            ingredient.Product.USDAProductStatusType = statusType;
          }
        }
      });
    },
  },
});

export const { selectAll: selectAllIngredients, selectById: selectIngredientById } = ingredientsAdapter.getSelectors(
  (state: EntityState<RecipeIngredientHeader>) => state
);

export const { selectAll: selectAllIngredientCategories } = ingredientCategoriesAdapter.getSelectors(
  (state: EntityState<RecipeIngredientCategory>) => state
);

export const { selectAll: selectAllIngredientProducts } = ingredientProductsAdapter.getSelectors(
  (state: EntityState<RecipeIngredientProduct>) => state
);

export const { selectAll: selectAllIngredientDiaries } = ingredientDiariesAdapter.getSelectors(
  (state: EntityState<RecipeIngredientDiary>) => state
);

export const { selectAll: selectAllIngredientCustomerDiaries } = ingredientCustomerDiariesAdapter.getSelectors(
  (state: EntityState<RecipeCustomerIngredientDiary>) => state
);

export const { selectAll: selectAllIngredientRecipes } = ingredientRecipesAdapter.getSelectors();

export const selectAllIngredientsCategoriesCollapsed = createSelector(selectAllIngredients, (ingredients) => {
  if (ingredients.length === 0) return false;
  return ingredients.filter((c) => !c.IsCollapsed).length === 0;
});

const getIngredients = (state: RootState): EntityState<RecipeIngredientHeader> => state.ingredients.ingredients;
const getFilterText = (_s: RootState, filterText: string | undefined) => filterText;

const filterIngredientHeaders = (
  ingredientHeaders: RecipeIngredientHeader[],
  filterText?: string
): RecipeIngredientHeader[] => {
  return filterObjects(ingredientHeaders, filterText || '', ['IngredientName', 'CategoryName']);
};

export const selectIngredientsGridData = createSelector([getIngredients, getFilterText], (ingredients, filterText) => {
  const collapsedCategoryIds = new Set<string | undefined>();
  const result = filterIngredientHeaders(selectAllIngredients(ingredients), filterText).filter((d) => {
    let include = true;

    if (!collapsedCategoryIds.has(d.RecipeIngredientCategoryId)) {
      collapsedCategoryIds.add(d.RecipeIngredientCategoryId);
    } else if (d.IsCollapsed) {
      include = false;
    }

    return include;
  });

  return result;
});

const getIngredientCategoryId = (_s: RootState, id: EntityId) => id;

export const selectIngredientCategoryIngredientCount = createSelector(
  [(state: RootState) => state.ingredients.ingredients, getIngredientCategoryId],
  (ingredients, categoryId) => {
    const headers = selectAllIngredients(ingredients); // Extract all entities
    const result = headers.filter((d) => d.RecipeIngredientCategoryId === categoryId).length;
    return result;
  }
);

const getIngredientDiary = (state: RootState): EntityState<RecipeIngredientDiary> =>
  state.ingredients.ingredientDiaries;

const filterIngredientDiary = (
  ingredientDiary: RecipeIngredientDiary[],
  filterText?: string
): RecipeIngredientDiary[] => {
  return filterObjects(ingredientDiary, filterText || '', ['ActionType', 'ActionSummary', 'ChangeByUser']);
};

export const selectIngredientDiary = createSelector(
  [getIngredientDiary, getFilterText],
  (ingredientDiary, filterText) => {
    return filterIngredientDiary(selectAllIngredientDiaries(ingredientDiary), filterText);
  }
);

const getIngredientCustomerDiary = (state: RootState): EntityState<RecipeCustomerIngredientDiary> =>
  state.ingredients.ingredientCustomerDiaries;

const filterIngredientCustomerDiary = (
  ingredientCustomerDiary: RecipeCustomerIngredientDiary[],
  filterText?: string
): RecipeCustomerIngredientDiary[] => {
  return filterObjects(ingredientCustomerDiary, filterText || '', ['ActionType', 'ActionSummary', 'ChangeByUser']);
};

export const selectIngredientCustomerDiary = createSelector(
  [getIngredientCustomerDiary, getFilterText],
  (ingredientCustomerDiary, filterText) => {
    return filterIngredientCustomerDiary(selectAllIngredientCustomerDiaries(ingredientCustomerDiary), filterText);
  }
);

export const selectCustomerIngredientCategories = createSelector(
  [selectAllIngredientCategories],
  (recipeCategories) => {
    return recipeCategories.sort((a, b) => a.CategoryName.localeCompare(b.CategoryName));
  }
);

const getIngredientsProdcutIds = (state: RootState): EntityId[] => state.ingredients.ingredientProducts.ids;
const getIngredientProductId = (_s: RootState, id: EntityId) => id;

export const selectRecipeIngredientProductIsCustomerPrimaryById = createSelector(
  [getIngredientsProdcutIds, getIngredientProductId],
  (ids, id) => {
    return ids?.[0] === id;
  }
);

export const selectHasManyRecipeIngredientProducts = createSelector([getIngredientsProdcutIds], (ids) => {
  return ids.length > 1;
});

export const selectIngredientsMostUsed = createSelector([selectAllIngredients], (headers) => {
  const result = headers
    .filter((d) => d.IsEditable)
    .sort((a, b) => (b.RecipesAssignedCount ?? 0) - (a.RecipesAssignedCount ?? 0))
    .splice(0, 4);

  return result;
});
